import { CSSProperties } from 'react'
import {
  computeImpact,
  displayImpact,
} from '@captaincause/lib/impact/computeImpact'
import { tm } from '../../tm'
import { Button } from '../../Button'
import { ContributionsCount } from '../../ContributionsCount'
import { ProgressBar } from '../../ProgressBar'
import i18n from './i18n'
import type { Amount, Locales, Project } from '@captaincause/types'

interface DonateProps {
  project: Project
  locale: Locales
  donate: (projectId: string) => void
  className?: string
  compact?: boolean
  progressBar?: boolean
  amount: Amount | undefined
  buttonStyle?: CSSProperties
}

interface DonateWithProgressBarProps extends DonateProps {
  progress: number
}

function DonateWithImpact({
  project,
  locale,
  amount,
  donate,
  compact = false,
  className = '',
}: DonateProps) {
  const t = i18n[locale]

  const impactNumItems = computeImpact({
    amount,
    action: project.action,
  })
  const displayedNumItems =
    impactNumItems && impactNumItems > 1 ? impactNumItems : 1

  const showImpact = impactNumItems && impactNumItems > 0 && project.action
  const showContributorsCount = project.contributors_count > 0

  return (
    <div className={className}>
      <div
        className={tm(
          'flex w-full justify-center gap-4 @[350px]:justify-between',
          compact && 'hidden',
        )}
      >
        {showContributorsCount ? (
          <ContributionsCount
            count={project.contributors_count}
            locale={locale}
            className="hidden @[350px]:flex"
            textClassName="text-dark-gray text-sm font-medium leading-5 -tracking-[0.14px]"
          />
        ) : null}
        {showImpact ? (
          <p
            className={tm(
              'line-clamp-1 w-full text-left text-sm font-normal leading-5 -tracking-[0.14px] text-dark-gray @[350px]:text-right',
              !showContributorsCount &&
                '@[350px]:max-w-full @[350px]:text-left',
            )}
          >
            {impactNumItems >= 1 ? t.fund : t.contribute}{' '}
            <span className="font-semibold">
              {displayImpact({
                action: project.action,
                value: { numItems: displayedNumItems },
                locale,
              })}
            </span>
          </p>
        ) : null}
      </div>
      <div className="mt-2 flex flex-col items-center gap-x-1 gap-y-2 @[350px]:flex-row @[350px]:gap-4">
        <div className="line-clamp-2 min-h-[48px] w-full flex-1 text-left text-base font-semibold leading-6 -tracking-[0.32px]">
          {project.name}
        </div>
        <Button
          onClick={() => donate(project.id)}
          breakout
          variant="primary"
          className="!h-[44px] w-full rounded-3xl px-4 py-3 text-base font-medium leading-5 -tracking-[0.14px] text-white @[350px]:w-fit"
        >
          {t.support}
        </Button>
      </div>
    </div>
  )
}

const emptyObject = {}
function DonateWithProgressBar({
  project,
  locale,
  donate,
  compact = false,
  progress,
  className = '',
  buttonStyle = emptyObject,
}: DonateWithProgressBarProps) {
  const t = i18n[locale]

  return (
    <div className={className}>
      <div
        className={tm(
          'flex w-full justify-center gap-4 @[350px]:justify-between',
          compact && 'hidden',
        )}
      >
        <div className="line-clamp-2 w-full flex-1 text-left text-base font-semibold leading-6 -tracking-[0.32px]">
          {project.name}
        </div>
      </div>
      <div className="mt-2 flex flex-col items-center gap-x-1 gap-y-2 @[350px]:flex-row @[350px]:gap-4">
        <ProgressBar percent={progress} locale={locale} />
        <Button
          onClick={() => donate(project.id)}
          breakout
          variant="primary"
          className="!h-[44px] w-full rounded-3xl px-4 py-3 text-base font-medium leading-5 -tracking-[0.14px] text-white @[350px]:w-fit"
          style={buttonStyle}
        >
          {t.support}
        </Button>
      </div>
    </div>
  )
}

export function Donate({
  project,
  locale,
  amount,
  donate,
  compact = false,
  progressBar = false,
  className = '',
  buttonStyle = emptyObject,
}: DonateProps) {
  const progress =
    project?.rolling_objective && project?.rolling_amount_collected_total
      ? 100 *
        (project.rolling_amount_collected_total.amount /
          project.rolling_objective.amount)
      : null
  const shouldUseProgressBar =
    progressBar && progress !== null && !Number.isNaN(progress) && progress >= 0

  return shouldUseProgressBar ? (
    <DonateWithProgressBar
      progress={progress}
      project={project}
      amount={amount}
      donate={donate}
      compact={compact}
      locale={locale}
      className={className}
      buttonStyle={buttonStyle}
    />
  ) : (
    <DonateWithImpact
      project={project}
      amount={amount}
      donate={donate}
      compact={compact}
      locale={locale}
      className={className}
      buttonStyle={buttonStyle}
    />
  )
}
